import { useEffect, useState } from 'react';
import DocumentShell, { DocField, DocSection } from '../../shared/documents/DocumentShell';
import PaymentSummary from '../../shared/documents/PaymentSummary';
import { Stars } from '../../shared/components/Common';
import { fmtDay, formatINR } from '../../shared/data/common';
import { getHotelBooking } from '../../api/hotels';
import { useAuth } from '../../auth/AuthContext';
import { freeCancellationText, penaltyLines } from './data';
import { fullAddress } from './HotelDetails';

const name = (t) => [t.title, t.firstName, t.lastName].filter(Boolean).join(' ');

/** Rooms, guests and the hotel's confirmation number from the supplier, for bookings made through the live API. */
function useLiveHotel(booking) {
  const { auth } = useAuth();
  const [live, setLive] = useState({ loading: Boolean(booking?.supplierBookingId), details: null });
  const id = booking?.supplierBookingId;
  const email = booking?.contact?.email;
  const token = auth?.accessToken;
  useEffect(() => {
    if (!id) return undefined;
    const controller = new AbortController();
    getHotelBooking(id, email, token, controller.signal)
      .then((details) => setLive({ loading: false, details }))
      .catch((error) => { if (error.name !== 'AbortError') setLive({ loading: false, details: null }); });
    return () => controller.abort();
  }, [id, email, token]);
  return live;
}

export default function HotelVoucher({ booking }) {
  const s = booking.summary;
  const { details } = useLiveHotel(booking);
  const confirmation = details?.confirmationNumber || (booking.ref !== booking.supplierBookingId ? booking.ref : null);
  const address = details?.address ? fullAddress(details.address) : s.address;
  const cancellation = details?.cancellation || s.cancellation;
  const free = cancellation ? freeCancellationText(cancellation) : s.freeCancel ? 'Free cancellation until 24 hours before check-in' : null;
  const checkInTime = details?.checkInFrom || s.checkInTime;
  const checkOutTime = details?.checkOutUntil || s.checkOutTime;
  const rooms = details?.rooms?.length
    ? details.rooms
    : Array.from({ length: s.rooms || 1 }, (_, i) => ({
      name: s.roomName,
      mealBasis: s.planName,
      adults: i === 0 ? s.adults : null,
      children: i === 0 ? s.children : null,
      guests: i === 0 ? booking.travellers.map(name) : [],
    }));
  const status = details?.status;
  const notice = booking.status === 'cancelled' || status === 'Cancelled'
    ? 'This booking has been cancelled. This voucher is not valid for check-in.'
    : status === 'CancellationPending'
      ? 'A cancellation has been requested for this booking. Contact support before travelling.'
      : booking.status === 'pending' || status === 'Pending'
        ? 'The hotel is still confirming this booking. Carry the voucher that shows the hotel confirmation number.'
        : null;

  return (
    <DocumentShell
      title="Hotel Voucher"
      subtitle={`${s.name} · ${fmtDay(s.checkin)}`}
      booking={booking}
      backTo={`/hotels/bookings/${booking.id}`}
      references={[
        ['Hotel confirmation no.', confirmation || (status === 'Confirmed' ? booking.supplierBookingId : 'Awaiting hotel'), true],
        ['Booking ID', booking.id],
        ...(booking.supplierBookingId ? [[`${booking.supplierCode || 'Supplier'} reference`, booking.supplierBookingId]] : []),
      ]}
      barcode={confirmation || booking.supplierBookingId || booking.id}
      notice={notice}
    >
      <DocSection title="Hotel">
        <div className="doc-hotel">
          {s.image && <img src={s.image} alt="" className="doc-hotel-img" />}
          <div>
            {(details?.starRating || s.stars) > 0 && <Stars n={details?.starRating || s.stars} />}
            <h3>{details?.hotelName || s.name}</h3>
            {address && <p>{address}</p>}
          </div>
        </div>
        <div className="doc-stay">
          <DocField label="Check-in">{`${fmtDay(s.checkin)}${checkInTime ? ` · from ${checkInTime}` : ''}`}</DocField>
          <DocField label="Check-out">{`${fmtDay(s.checkout)}${checkOutTime ? ` · until ${checkOutTime}` : ''}`}</DocField>
          <DocField label="Stay">{`${s.nights} night${s.nights > 1 ? 's' : ''} · ${s.rooms || rooms.length} room${(s.rooms || rooms.length) > 1 ? 's' : ''}`}</DocField>
          <DocField label="Guests">{`${s.adults} adult${s.adults > 1 ? 's' : ''}${s.children ? `, ${s.children} child${s.children > 1 ? 'ren' : ''}` : ''}`}</DocField>
        </div>
      </DocSection>

      <DocSection title="Rooms & guests">
        <table className="doc-table">
          <thead><tr><th>Room</th><th>Room type</th><th>Meal plan</th><th>Occupancy</th><th>Guests</th></tr></thead>
          <tbody>
            {rooms.map((room, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td><b>{room.name}</b></td>
                <td>{room.mealBasis || 'Room only'}</td>
                <td>{room.adults != null ? `${room.adults} adult${room.adults === 1 ? '' : 's'}${room.children ? `, ${room.children} child${room.children === 1 ? '' : 'ren'}` : ''}` : '—'}</td>
                <td>{room.guests.join(', ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="doc-small">Lead guest: <b>{name(booking.travellers[0] || {}) || '—'}</b> · {booking.contact.phone} · {booking.contact.email}</p>
      </DocSection>

      <div className="doc-columns">
        <DocSection title="Cancellation policy">
          <p className={free ? 'doc-ok' : 'doc-warn'}><b>{free || 'Non-refundable rate'}</b></p>
          {cancellation && (
            <ul className="doc-list">{penaltyLines(cancellation, formatINR).map((l) => <li key={l}>{l}</li>)}</ul>
          )}
          <p className="doc-small">No-shows are charged the full amount. The convenience fee is not refundable.</p>
        </DocSection>
        <PaymentSummary booking={booking} />
      </div>

      <DocSection title="Important information" className="doc-info">
        <ul>
          <li>Show this voucher (printed or on your phone) with a valid government photo ID for every guest at check-in.</li>
          <li>The hotel may ask for a refundable security deposit or charge local fees and taxes that are not included in this booking.</li>
          <li>Early check-in and late check-out depend on availability and may be charged by the hotel.</li>
          <li>Special requests are passed to the hotel but are not guaranteed.</li>
          <li>This booking is not transferable. The lead guest must be at least 18 years old.</li>
          <li>To change or cancel, use My Trips or call support. Cancellations follow the policy above.</li>
        </ul>
      </DocSection>
    </DocumentShell>
  );
}
